import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box, useMediaQuery, useTheme, Button, Typography, Divider } from '@mui/material';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import LogoutIcon from '@mui/icons-material/Logout';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';

import * as AuthActions from '../../../../../store/actions/auth.action';
import { getUser } from '../../../../../store/selector/auth.selector';
import ConfirmationModal from '../../modal/confirmation-modal';
import SidenavItem from './sidenav-item';
import { IModal } from '../../../../interfaces/modal.interface';
import DashboardIcon from '@mui/icons-material/Dashboard';
import Logo from '../../../../assets/images/logowhite.png';

const styles = {
  sideNavContent: (isSideNavOpen: boolean) => ({
    display: 'flex',
    justifyContent: 'center',
    position: 'fixed',
    background: '#02004d',
    height: '100vh',
    width: '16.25rem',
    borderRight: '0.0625rem solid rgba(0, 0, 0, 0.1)',
    paddingY: 5,
    transition: 'all .7s ease',
    overflowY: 'auto',
    left: isSideNavOpen ? '0px' : '-16.25rem',
    zIndex: 10,
  }),

  navContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'left',
    flexDirection: 'column',
  },
};

const SideBar = ({ isSideNavOpen }: { isSideNavOpen: boolean }) => {
  const dispatch = useDispatch();
  const user = useSelector(getUser);
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [hrRestriction, setHrRestriction] = useState<boolean>(true);
  const [modalOptions, setModalOptions] = useState<IModal>({
    isModalOpen: false,
    title: 'Confirmation',
    message: 'Your account will be logged out of the portal. Are you sure you want to proceed?',
    onClickModal: (e: string) => onModalAction(e),
  });

  const openModal = () => {
    setModalOptions((p) => ({
      ...p,
      isModalOpen: true,
    }));
  };

  const onModalAction = (e: string) => {
    setModalOptions((p) => ({ ...p, isModalOpen: false }));

    if (e === 'confirm') dispatch(AuthActions.Logout());
  };

  useEffect(() => {
    if (user) {
      const { groups } = user;

      switch (true) {
        case groups.includes('hr-bp'):
        case groups.includes('hr-view-only'):
          setHrRestriction(true);
          break;

        default:
          setHrRestriction(false);
          break;
      }
    }
  }, [user]);

  return (
    <Box data-testid='sidebar' sx={styles.sideNavContent(isSideNavOpen)}>
      <Box sx={{ width: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            mb: isTablet ? '3rem' : '8rem',
          }}
        >
          <Box component='img' src={Logo} sx={{ width: '8rem' }} />
        </Box>

        <Box sx={styles.navContainer}>
          {isTablet && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'column',
                mb: '5rem',
                paddingX: '1rem',
              }}
            >
              <Box
                sx={{
                  width: '5rem',
                  height: '5rem',
                  borderRadius: '50%',
                  background: 'white',
                  border: '1px solid #dbdbdb',
                  overflow: 'visible',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: '2rem',
                }}
              >
                <Typography sx={{ fontSize: '1.875rem' }}>
                  {user?.email.charAt(0).toUpperCase()}
                </Typography>
              </Box>

              <Typography
                sx={{
                  fontSize: '1rem',
                  color: '#ffffff',
                  wordBreak: 'break-word',
                  textAlign: 'center',
                }}
              >
                {user?.email}
              </Typography>
            </Box>
          )}

          {user.is_hr && (
            <SidenavItem
              label='Dashboard'
              path='/dashboard'
              icon={<DashboardIcon sx={{ mr: '1rem', fontSize: '1.25rem' }} />}
            />
          )}

          {user.is_hr && (
            <SidenavItem
              label='HR'
              path='/hr'
              icon={<AdminPanelSettingsIcon sx={{ mr: '1rem', fontSize: '1.25rem' }} />}
            />
          )}

          {!hrRestriction && user.groups.length > 1 && (
            <SidenavItem
              label='Approvals'
              path='/approvals'
              icon={<SupervisorAccountIcon sx={{ mr: '1rem', fontSize: '1.25rem' }} />}
            />
          )}

          <Divider variant='middle' sx={{ bgcolor: '#ffffff40', marginY: '2rem' }} />

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginX: '0.5rem',
              borderRadius: '4px',
              color: '#ffffff',
              textDecoration: 'none',
            }}
          >
            <Button
              fullWidth
              sx={{
                display: 'flex',
                paddingX: '0.75rem',
                justifyContent: 'flex-start',
                color: '#ffffff',
                textTransform: 'none',
                mb: '5rem',
              }}
              onClick={openModal}
              disableElevation
            >
              <LogoutIcon sx={{ mr: '1rem', fontSize: '1.25rem' }} />

              <Typography sx={{ fontSize: '0.875rem' }}>Logout</Typography>
            </Button>
          </Box>
        </Box>
      </Box>
      <ConfirmationModal
        isModalOpen={modalOptions.isModalOpen}
        title={modalOptions.title}
        message={modalOptions.message}
        onClickModal={modalOptions.onClickModal}
      />
    </Box>
  );
};

export default SideBar;
